import * as React from "react";
import { ButtonType, CustomButton, Table, Modal, ModalBody, ModalFooter, CustomSubmitButton, Form, Checkbox, TextInput, AlertMessage, AlertType } from "soprasteria-ui-library";
import { useRouteMatch, useHistory } from "react-router";
import { Link } from "react-router-dom";
import { ORTHODONTIC_REFERRAL } from "../../../../utils/RouteConstants";
import { IOrthodonticReferral, IOrthodonticReferralCreate } from "../../../../models/IOrthodonticReferral";
import { dateTimeToDateString } from "../../../../helpers/DateHelper";
import { NotAllowedModal } from "../Modals/NotAllowedModal";
import { OrthodonticReferralTreatmentApi } from "../../../../api/OrthodonticReferralTreatmentApi";
import { OrthodonticReferralAdministrationApi } from "../../../../api/OrthodonticReferralAdministrationApi";
import { useAuth } from "../../../../App";
import { TREATMENT1_TYPEID, TREATMENT2_TYPEID } from "../../../../utils/OrthodonticReferralConstants";
import { setMessage } from "../../../../helpers/MessageHelper";

interface IOrthodonticReferralTab {
    canCreateNewOrthodonticReferral: boolean;
    orthodonticReferrals: IOrthodonticReferral[];
    isInRoleAllmäntandvård: boolean;
    isRoleOrthodontist: boolean;
    isPatientEntitledToOrthodonticCare: boolean;
    patientNotEntitledToOrthodonticCareReason: string;
    reloadData: () => void;
    isLoading: (loading: boolean) => void;
}

export const OrthodonticReferralTab: React.FunctionComponent<IOrthodonticReferralTab> = ({ canCreateNewOrthodonticReferral, orthodonticReferrals, isInRoleAllmäntandvård, isRoleOrthodontist, isPatientEntitledToOrthodonticCare, patientNotEntitledToOrthodonticCareReason, reloadData, isLoading }) => {

    let match = useRouteMatch();
    let modalId = "not-allowed-to-see-orthodontic-referral-modal";
    const auth = useAuth();
    const api = new OrthodonticReferralTreatmentApi(auth);
    const administrationApi = new OrthodonticReferralAdministrationApi(auth);
    const [selectedReferral, setSelectedReferral] = React.useState<IOrthodonticReferral>();
    const history = useHistory();
    const [renderForm, setRenderForm] = React.useState<boolean>(true);
    const createOrthodonticReferralState: IOrthodonticReferralCreate = { orthodonticReferralTypeId: isRoleOrthodontist ? TREATMENT2_TYPEID : TREATMENT1_TYPEID }
    const [notAllowedModalText, setNotAllowedModalText] = React.useState<string>("");

    const [anyDeletableOrthodonticReferrals, setanyDeletableOrthodonticReferrals] = React.useState<boolean>(false);
    const [successMessage, setSuccessMessage] = React.useState<string>("");
    const [errorMessage, setErrorMessage] = React.useState<string>("");

    React.useEffect(() => {
        setanyDeletableOrthodonticReferrals(orthodonticReferrals.some(r => r.canBeDeleted === true));
    }, [orthodonticReferrals])

    const renderTableLink = (linkText: string, orthodonticReferral: IOrthodonticReferral) => {
        if (orthodonticReferral.isAllowedToAccessOrthodonticReferral) {
            return <Link to={`${match.url}/${ORTHODONTIC_REFERRAL}/${orthodonticReferral.orthodonticReferralId}`}>{linkText}</Link>
        }
        else if (orthodonticReferral.canTakeResponsibilityForTreatment) {
            return <a data-toggle="modal" data-target="#take-resposiblity-for-treatment-modal"
                onClick={() => { setSelectedReferral(orthodonticReferral); setRenderForm(true); }}>{linkText}</a>
        }
        else {
            return <a data-toggle="modal" data-target={`#${modalId}`} onClick={() => getNotAllowedModalText(orthodonticReferral?.isOrthodonticDecisionOlderThan180Days)}>{linkText}</a>
        }
    }

    const removeOrthodonticReferral = (orthodonticReferralId: number | undefined) => {
        const accepted = window.confirm("Är du säker på att du vill radera Ortodontiremiss?");
        if (accepted) {
            isLoading(true);
            if (orthodonticReferralId) {
                administrationApi.deleteOrthodonticReferral(orthodonticReferralId.toString(), () => {
                    changeSuccessMessage("Ortodontiremiss raderad");
                    reloadData();
                }, (error) => {
                    changeErrorMessage(error);
                }, () => {
                    isLoading(false);
                });
            }
        }
    }

    const changeSuccessMessage = (message: string) => {
        const newMessage = setMessage(message, successMessage ?? "");
        setSuccessMessage(newMessage);
    }

    const changeErrorMessage = (message: string) => {
        const newMessage = setMessage(message, errorMessage ?? "");
        setErrorMessage(newMessage);
    }

    const takeResposiblityForTreatment = () => {
        api.takeResposiblityForTreatment(selectedReferral?.orthodonticReferralId ?? 0, () => {
            document.getElementById("close-take-resposiblity-for-treatment-modal")?.click();
            history.push(`${match.url}/${ORTHODONTIC_REFERRAL}/${selectedReferral?.orthodonticReferralId}`);
        });
    }

    const getNotAllowedModalText = (isOrthodonticDecisionOlderThan180Days?: boolean) => {
        if (isInRoleAllmäntandvård) {
            setNotAllowedModalText("Begäran om aktivt medgivande har skickats till remittenten. Begäran har ännu inte bifallits.");
        }
        else if (isOrthodonticDecisionOlderThan180Days) {
            setNotAllowedModalText("Detta vårderbjudandes giltighetstid på 180 dagar har gått ut. Kontakta tandvårdsenheten för begäran om förlängning");
        }
        else {
            setNotAllowedModalText("Du har ej behörighet att se den valda remissen");
        }
    }

    return (
        <section>
            {
                canCreateNewOrthodonticReferral ?
                    <div className="panel text-right">
                        {
                            isPatientEntitledToOrthodonticCare
                                ? <Link to={{ pathname: `${match.url}/${ORTHODONTIC_REFERRAL}`, state: createOrthodonticReferralState }}>
                                    <CustomButton buttonText="Skapa ortodontiremiss" buttonType={ButtonType.create} />
                                  </Link>
                                : <CustomButton buttonType={ButtonType.create} disabled buttonText="Skapa ortodontiremiss"
                                    title={patientNotEntitledToOrthodonticCareReason} />
                        }
                    </div>
                    : null
            }
            <NotAllowedModal notAllowedText={notAllowedModalText} id={modalId} />
            <span className="d-none">
                <CustomButton buttonText="" buttonType={ButtonType.success} data-toggle="modal" data-target="#take-resposiblity-for-treatment-modal" id="open-take-resposiblity-for-treatment-modal" />
            </span>
            <Modal id="take-resposiblity-for-treatment-modal" header="Registrering av ansvar för ortodontiremiss">
                {
                    renderForm ?
                        <Form onSubmit={takeResposiblityForTreatment}>
                            <ModalBody>
                                <p>Genom att öppna den här ortodontiremissen bekräftar du att du tar över vårdansvaret för patienten
                                    {
                                        selectedReferral?.orthodontistCareProvider ?
                                            <> från <b>{selectedReferral.orthodontistCareProvider.name}</b></>
                                            : "."
                                    }
                                </p>
                                <Checkbox label="Vårdansvar tas över efter överenskommelse med patienten" name="agreement" id="agreement" value="true" required inputCol={2} labelCol={10} />
                            </ModalBody>
                            <ModalFooter>
                                <CustomButton buttonType={ButtonType.general} data-dismiss="modal" buttonText="Avbryt" id="close-take-resposiblity-for-treatment-modal" onClick={() => setRenderForm(false)} />
                                <CustomSubmitButton>Registrera vårdansvar</CustomSubmitButton>
                            </ModalFooter>
                        </Form>
                        : null
                }
            </Modal>
            <AlertMessage alertType={AlertType.danger} duration={3000}>{errorMessage}</AlertMessage>
            <AlertMessage alertType={AlertType.success} duration={3000}>{successMessage}</AlertMessage>
            <Table hoverableRows>
                <thead>
                    <tr>
                        <th>Datum skickad</th>
                        <th>Remiss-ID</th>
                        <th>Urvalstandläkare</th>
                        <th>Status</th>

                        {
                            anyDeletableOrthodonticReferrals ?
                                <th className="delete-btn">Ta bort</th>
                                : null
                        }
                    </tr>
                </thead>
                <tbody>
                    {
                        orthodonticReferrals.length > 0 ?
                            orthodonticReferrals.map((orthodonticReferral, key) => {
                                return (
                                    <tr key={key} className="clickable-row">
                                        <td className="table-data-link">{renderTableLink(orthodonticReferral.sentDate ? dateTimeToDateString(orthodonticReferral.sentDate) : "", orthodonticReferral)}</td>
                                        <td className="table-data-link">{renderTableLink(orthodonticReferral.orthodonticReferralNumber ?? "", orthodonticReferral)}</td>
                                        <td className="table-data-link">{renderTableLink(orthodonticReferral.consultantCareProvider.name ?? "", orthodonticReferral)}</td>
                                        <td className="table-data-link">{renderTableLink(orthodonticReferral.orthodonticReferralStatus ?? "", orthodonticReferral)}</td>
                                        {
                                            anyDeletableOrthodonticReferrals ?
                                                <td className="delete-btn table-data-link">
                                                    {
                                                        orthodonticReferral.canBeDeleted ?
                                                            <CustomButton
                                                                buttonType={ButtonType.delete}
                                                                onClick={() => removeOrthodonticReferral(orthodonticReferral.orthodonticReferralId)}
                                                                buttonText="" />
                                                            : null
                                                    }
                                                </td>
                                                : null
                                        }
                                    </tr>
                                )
                            })
                            :
                            <tr><td colSpan={anyDeletableOrthodonticReferrals ? 5 : 4}>Det finns inga ortodontiremisser registrerade för den här patienten</td></tr>
                    }
                </tbody>
            </Table>
        </section>
    )
}